import { Box, Grid, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import React, { useEffect, useRef, useState } from 'react';
import { SortableHandle as sortableHandle } from 'react-sortable-hoc';

import { VerticalEllipsisIcon } from '../../../assets/Svg';
import FormTextField, { IFormTextFieldImperativeHandle } from '../../../components/FormTextField';
import { CopyToClipboard } from '../../../components/CopyToClipboard';
import { IOnboardingCollectionMapper } from '../../../services/types';
import { convertFirstCharacterToUppercase } from '../../../services/utils';
import theme from '../../../theme';

interface IProps {
  collectionMapper: Partial<IOnboardingCollectionMapper>;
  isEditing: boolean;
  onChange: (mapper: Partial<IOnboardingCollectionMapper>) => void;
  onDelete: () => void;
  onMount: (themeNameRef: React.MutableRefObject<IFormTextFieldImperativeHandle | undefined>) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    zIndex: 1,
    padding: 20,
    borderBottom: '1px solid #dcdcdc',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  thumbWrap: {
    flex: '0 0 99px',
    alignSelf: 'flex-start',
  },
  infoWrap: {
    flex: '1',
    alignSelf: 'flex-start',
  },
  infoLabel: {
    marginBottom: 6,
    fontSize: 12,
    lineHeight: '16px',
    color: 'rgba(0, 0, 0, 0.38)',
  },
  infoText: {
    minHeight: 30,
  },
  infoItem: {
    paddingRight: 16,
    marginBottom: 19,
  },
  infoItemBottom: {
    paddingRight: 16,
    marginBottom: 0,
  },
  dragButton: {
    position: 'absolute',
    left: -5,
  },
  deleteButton: {
    cursor: 'pointer',
  },
  coverImg: {
    minWidth: 83,
    width: 83,
    height: 122,
    objectFit: 'cover',
    borderRadius: 6,
  },
}));

const DragHandle = sortableHandle(() => <VerticalEllipsisIcon />);

const CollectionMapper = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const { collectionMapper, isEditing, onChange, onDelete } = props;
  const season = collectionMapper.collection?.stories[0]?.seasons[0];

  const themeNameRef = useRef<IFormTextFieldImperativeHandle>();

  const handleDeleteClick = () => {
    onDelete();
  };

  useEffect(() => {
    props.onMount(themeNameRef);
  }, []);

  return (
    <Box className={classes.container}>
      {isEditing && (
        <span className={classes.dragButton}>
          <DragHandle />
        </span>
      )}
      <Box className={classes.thumbWrap}>
        <img
          src={season && season.coverUrl ? `${season.coverUrl}?w=150` : `${process.env.CDN_URL}/static/cms/storycover-empty-v.png`}
          className={classes.coverImg}
        />
      </Box>
      <Grid container className={classes.infoWrap}>
        <Grid item xs={6} className={classes.infoItem}>
          <Typography className={classes.infoLabel}>Tracking ID</Typography>
          <Box className={classes.infoText}>
            <CopyToClipboard content={collectionMapper.collection?.trackingId || ''} />
          </Box>
        </Grid>
        <Grid item xs={3} className={classes.infoItem}>
          <Typography className={classes.infoLabel}>Content Type</Typography>
          <Box className={classes.infoText}>
            {collectionMapper.collection?.storyTypes?.map(storyType => convertFirstCharacterToUppercase(storyType)).join(', ') || '-'}
          </Box>
        </Grid>
        <Grid item xs={3} className={classes.infoItem}>
          <Typography className={classes.infoLabel}>Countries</Typography>
          <Box className={classes.infoText}>{collectionMapper.collection?.availableCountryCountText || '-'}</Box>
        </Grid>
        <Grid item xs={6} className={classes.infoItem}>
          <Typography className={classes.infoLabel}>Segment ID</Typography>
          <Box className={classes.infoText}>
            <FormTextField
              defaultValue={String(collectionMapper.segmentId || '')}
              name="Segment Id"
              placeholder="-"
              isEditing={isEditing}
              fullWidth
              onValueSubmit={(segmentId: string) => {
                onChange({ ...collectionMapper, segmentId: segmentId ? Number(segmentId) : null });
              }}
              className={''}
              InputProps={{ type: 'number' }}
            />
          </Box>
        </Grid>
        <Grid item xs={6} className={classes.infoItem}>
          <Typography className={classes.infoLabel}>Collection Title</Typography>
          <Box className={classes.infoText}>{collectionMapper.collection?.title}</Box>
        </Grid>
        <Grid item xs={6} className={classes.infoItem}>
          <Typography className={classes.infoLabel}>Representative Story</Typography>
          <Box className={classes.infoText}>{collectionMapper.collection?.stories[0]?.title}</Box>
        </Grid>
        <Grid item xs={6} className={classes.infoItemBottom}>
          <Typography className={classes.infoLabel}>Theme Name</Typography>
          <Box className={classes.infoText}>
            <FormTextField
              defaultValue={String(collectionMapper.themeName || '')}
              name="Theme Name"
              placeholder="Theme Name"
              isEditing={isEditing}
              required={true}
              ref={themeNameRef}
              fullWidth
              onValueSubmit={(themeName: string) => {
                onChange({ ...collectionMapper, themeName });
              }}
              maxLength={18}
              showTextCount={true}
              className={''}
            />
          </Box>
        </Grid>
      </Grid>
      {isEditing && (
        <span className={classes.deleteButton}>
          <DeleteIcon onClick={handleDeleteClick} color={'primary'} />
        </span>
      )}
    </Box>
  );
};

export default CollectionMapper;
